import { useEffect, useState } from "react";
import { ExternalLink } from "lucide-react";
import api, { imgSrc } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import PartnerModal from "@/components/PartnerModal";
import { useLang } from "@/context/LanguageContext";

const monogram = (name) => name.split(" ").filter(Boolean).map((w) => w[0]).join("").slice(0, 2).toUpperCase();

function SponsorCard({ item, tag, delay, onOpen }) {
  const { t } = useLang();
  const logo = item.logo_url ? imgSrc(item.logo_url) : null;
  return (
    <Reveal delay={delay} className="h-full">
      <div className="group relative h-full rounded-2xl border border-slate-800/80 bg-slate-900/50 backdrop-blur-md transition-all duration-300 hover:border-amber-400/50 hover:-translate-y-1">
        <button
          data-testid={`sponsor-card-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
          onClick={() => onOpen({ name: item.name, tagline: tag, logo_url: item.logo_url, intro: item.description, photos: item.photos, link: item.link })}
          className="h-full w-full p-5 text-center focus:outline-none"
        >
          {logo ? (
            <span className="mx-auto flex h-14 w-auto min-w-14 max-w-44 items-center justify-center rounded-xl bg-white px-2.5 py-2 shadow-[0_6px_20px_rgba(0,0,0,0.35)] transition-transform duration-300 group-hover:scale-105">
              <img src={logo} alt={`Logo ${item.name}`} loading="lazy" className="max-h-full max-w-full object-contain" />
            </span>
          ) : (
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-xl border border-slate-700 bg-[#0C1220] font-display text-2xl text-slate-200 transition-colors duration-300 group-hover:border-amber-400/60 group-hover:text-amber-400">
              {monogram(item.name)}
            </span>
          )}
          <span className="mt-3 block text-xs sm:text-sm font-bold uppercase tracking-[0.12em] text-slate-100">{item.name}</span>
          <span className="mt-0.5 block text-[9px] uppercase tracking-[0.2em] text-amber-400/90">{tag}</span>
        </button>
        {item.link && (
          <a
            href={item.link}
            target="_blank"
            rel="noreferrer"
            data-testid={`sponsor-site-${item.name.toLowerCase().replace(/\s+/g, "-")}`}
            aria-label={`${t("modal.visit")}: ${item.name}`}
            className="absolute right-2.5 top-2.5 flex h-8 w-8 items-center justify-center rounded-full border border-slate-700 bg-[#090D14]/70 text-slate-400 transition-colors duration-300 hover:border-sky-400/60 hover:text-sky-300"
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        )}
      </div>
    </Reveal>
  );
}

export default function Sponsor() {
  const { t } = useLang();
  const [sponsors, setSponsors] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/sponsors").then((r) => setSponsors(r.data)).catch(() => setSponsors([]));
  }, []);

  const main = (sponsors || []).filter((s) => s.kind === "sponsor");
  const tech = (sponsors || []).filter((s) => s.kind === "tecnico");

  return (
    <section id="sponsor" className="py-12 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <span className="text-xs uppercase tracking-[0.3em] font-semibold text-sky-400">{t("sponsor.eyebrow")}</span>
          <h2 className="font-display mt-4 text-4xl sm:text-5xl lg:text-6xl text-slate-50">
            {t("sponsor.titleA")}<span className="text-amber-400">{t("sponsor.titleAccent")}</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-slate-400 leading-relaxed">
            {t("sponsor.subtitle")}
          </p>
        </Reveal>

        <Reveal delay={0.1} className="mt-10">
          <h3 className="font-display text-2xl sm:text-3xl text-slate-50 flex items-center gap-4">
            <span className="h-px w-10 bg-sky-400/70" />
            {t("sponsor.mainTitle")}
          </h3>
        </Reveal>
        <div className="mt-5 grid grid-cols-3 md:grid-cols-3 gap-3 max-w-3xl" data-testid="sponsor-main-grid">
          {main.map((s, i) => <SponsorCard key={s.id} item={s} tag={t("sponsor.tagMain")} delay={0.05 * i} onOpen={setSelected} />)}
        </div>

        <Reveal delay={0.1} className="mt-10">
          <h3 className="font-display text-2xl sm:text-3xl text-slate-50 flex items-center gap-4">
            <span className="h-px w-10 bg-amber-400/70" />
            {t("sponsor.techTitle")}
          </h3>
        </Reveal>
        <div className="mt-5 grid grid-cols-3 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="sponsor-tech-grid">
          {tech.map((s, i) => <SponsorCard key={s.id} item={s} tag={t("sponsor.tagTech")} delay={0.05 * i} onOpen={setSelected} />)}
        </div>

        {sponsors === null && <p className="mt-8 text-sm text-slate-500">{t("sponsor.loading")}</p>}
      </div>

      <PartnerModal item={selected} onClose={() => setSelected(null)} />
    </section>
  );
}
